/**
 * LeagueActivityFeed — Recent transactions across all user leagues
 * Style C tiles with action icon badges
 */
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { leaguesAPI } from '../../../services/apiService';
import MobilePlayerLink from './MobilePlayerLink';
import { dynastyTheme } from '../../../services/colorService';
import MobileSectionHeader from './MobileSectionHeader';

const C = dynastyTheme.tokens.colors;
const HAIRLINE = `1px solid ${C.hairline}`;

const getRelativeTime = (dateStr) => {
  if (!dateStr) return '';
  const mins = Math.floor((Date.now() - new Date(dateStr).getTime()) / 60000);
  if (Number.isNaN(mins)) return '';
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d ago`;
  return `${Math.floor(days / 7)}w ago`;
};

// Badge type mapping — colors from the central transaction-activity ramp (txn.*).
const BADGE_ADD = {
  bg: C.txn.add.tint,
  stroke: C.txn.add.stroke,
  icon: (
    <svg viewBox="0 0 24 24" fill="none" stroke={C.txn.add.stroke} strokeWidth="2.5" width="12" height="12">
      <line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>
    </svg>
  ),
  verb: 'added',
};
const BADGE_DROP = {
  bg: C.txn.drop.tint,
  stroke: C.txn.drop.stroke,
  icon: (
    <svg viewBox="0 0 24 24" fill="none" stroke={C.txn.drop.stroke} strokeWidth="2.5" width="12" height="12">
      <line x1="5" y1="12" x2="19" y2="12"/>
    </svg>
  ),
  verb: 'released',
};
const BADGE_MOVE = {
  bg: C.txn.move.tint,
  stroke: C.txn.move.stroke,
  icon: (
    <svg viewBox="0 0 24 24" fill="none" stroke={C.txn.move.stroke} strokeWidth="2.5" width="12" height="12">
      <path d="M5 12h14M12 5l7 7-7 7"/>
    </svg>
  ),
  verb: 'moved',
};
const BADGE_TRADE = {
  bg: C.txn.trade.tint,
  stroke: C.txn.trade.stroke,
  icon: (
    <svg viewBox="0 0 24 24" fill="none" stroke={C.txn.trade.stroke} strokeWidth="2.5" width="12" height="12">
      <path d="M4 12h16M8 7l-4 5 4 5M16 7l4 5-4 5"/>
    </svg>
  ),
  verb: 'traded',
};

const TYPE_MAP = {
  free_agent: BADGE_ADD, fa_acquisition: BADGE_ADD, commissioner_add: BADGE_ADD,
  draft_pick: BADGE_ADD, minor_draft_pick: BADGE_ADD, autodraft: BADGE_ADD,
  minor_promotion: BADGE_ADD, keeper_selection: BADGE_ADD,
  release: BADGE_DROP, offseason_reset: BADGE_DROP, draft_pick_reversal: BADGE_DROP,
  trade: BADGE_TRADE,
  contract_change: BADGE_MOVE, roster_move: BADGE_MOVE,
};

const getBadge = (type) => TYPE_MAP[type] || BADGE_MOVE;

const badgeBaseStyle = { width: 20, height: 20, borderRadius: 5, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 };

function LeagueActivityFeed({ leagues, defaultLeagueId, onPlayerClick }) {
  const navigate = useNavigate();
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    if (leagues && leagues.length > 0) fetchAllTransactions();
  }, [leagues]);

  const fetchAllTransactions = async () => {
    try {
      setLoading(true);
      const results = await Promise.all(
        leagues.map(async (league) => {
          try {
            const res = await leaguesAPI.getTransactions(league.league_id, { limit: 25, days_back: 14 });
            return (res?.transactions || res?.data || []).map(t => ({ ...t, _league_name: league.league_name, _league_id: league.league_id }));
          } catch { return []; }
        })
      );
      setTransactions(
        results.flat().filter(t => !t.is_reversed)
          .sort((a, b) => new Date(b.transaction_date) - new Date(a.transaction_date))
          .slice(0, 20)
      );
    } catch { /* ignore */ }
    finally { setLoading(false); }
  };

  const handleSeeAll = () => {
    const target = defaultLeagueId || (leagues.length > 0 ? leagues[0].league_id : null);
    if (target) navigate(`/leagues/${target}`, { state: { initialPage: 'transactions' } });
  };

  const getTeamName = (txn) => txn.to_team_name || txn.from_team_name || '';
  const getTeamId = (txn) => txn.to_team_id || txn.from_team_id || null;

  const handleTeamClick = (txn, e) => {
    e.stopPropagation();
    const teamId = getTeamId(txn);
    const teamName = getTeamName(txn);
    if (teamId && txn._league_id) {
      navigate(`/leagues/${txn._league_id}/team`, {
        state: { targetPage: 'roster', targetTeamId: teamId, targetTeamName: teamName }
      });
    }
  };

  const handlePlayerTap = (txn, e) => {
    e.stopPropagation();
    if (txn.player_id && onPlayerClick) {
      onPlayerClick(txn.player_id);
    }
  };

  if (loading) {
    return (
      <div style={{ borderTop: HAIRLINE }}>
        <MobileSectionHeader title="League Activity" accent={dynastyTheme.tokens.colors.success} />
        <div style={{ padding: '16px 8px', color: C.muted, fontSize: 12 }}>Loading activity…</div>
      </div>
    );
  }

  return (
    <div style={{ borderTop: HAIRLINE }}>
      <MobileSectionHeader
        title="League Activity"
        accent={dynastyTheme.tokens.colors.success}
        action={transactions.length > 0 ? { label: 'See all', onClick: handleSeeAll } : undefined}
        chevron={transactions.length > 0 ? { expanded, onClick: () => setExpanded(v => !v) } : undefined}
      />

      {transactions.length === 0 ? (
        <div style={{ padding: '16px 8px', color: C.muted, fontSize: 12 }}>No recent activity</div>
      ) : (
        <div
          className="home-scroll"
          style={{ maxHeight: expanded ? 340 : 150, overflowY: 'auto', scrollbarWidth: 'none', msOverflowStyle: 'none', WebkitOverflowScrolling: 'touch', padding: '0 8px' }}
        >
          {transactions.map((txn, i) => {
            const badge = getBadge(txn.transaction_type);
            const ago = getRelativeTime(txn.transaction_date);
            return (
              <div key={txn.transaction_id || i} style={{
                display: 'flex', alignItems: 'center', gap: 8, padding: '6px 0',
                borderBottom: i < transactions.length - 1 ? HAIRLINE : 'none',
              }}>
                <div style={{ ...badgeBaseStyle, background: badge.bg }}>
                  {badge.icon}
                </div>
                <p style={{ flex: 1, minWidth: 0, fontSize: 10, lineHeight: '1.2', margin: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  <span
                    onClick={(e) => handleTeamClick(txn, e)}
                    style={{ color: C.grayCool, fontWeight: 500, cursor: getTeamId(txn) ? 'pointer' : 'default' }}
                  >{getTeamName(txn)}</span>
                  {' '}<span style={{ color: C.mutedLight }}>{badge.verb}</span>{' '}
                  <MobilePlayerLink
                    mlbId={txn.player_id}
                    name={txn.player_name || 'Unknown'}
                    onClick={onPlayerClick ? () => onPlayerClick(txn.player_id) : null}
                    style={{ fontWeight: 600 }}
                  />
                  <span style={{ color: C.muted, fontSize: 9 }}>{' · '}{txn._league_name}{ago ? ` · ${ago}` : ''}</span>
                </p>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default LeagueActivityFeed;
